/* -----------------UI--------------- */
import type { IconType } from "components";

import {
	getItemTypeProgressData,
	getMasteryTagsProgressData,
} from "@helpers/progress-data";
/* -----------------Helpers & Hooks--------------- */

/* -----------------Types--------------- */
import type { ReportDetails } from "@memorang/types";

/* -----------------Child components--------------- */
import theme from "@configs/theme";
import { isScreenshotMode } from "@constants/common";
import {
	mockedMasteryCardData,
	mockedSummaryCardData,
	mockedTagProgressData,
} from "@constants/mocked-data";

import { isMockedData, mockedReport } from "@constants/demo";
import { sortedMasteryLevels } from "@helpers/content/distribution";
import { smartPluralize } from "@helpers/smartPluralize";
import type { AppTheme } from "@hooks/useAppTheme";
import type { TFunction } from "i18next";
import {
	type Skill,
	type SkillDetails,
	getCompositeScoreAndLevel,
} from "./points-normalization";

export const getSessionReportInfoText = ({
	numActivities,
	numPointsEarned,
	numTotalPoints,
	skills,
	compositeLevel,
	compositeFeedback,
	t,
}: {
	numActivities: number;
	numPointsEarned: number;
	numTotalPoints: number;
	skills: SkillDetails[];
	compositeLevel: string;
	compositeFeedback: string;
	t: TFunction;
}) => {
	const skillNames = skills.map((s) =>
		t(`dashboard.skills.${s.skill}`).toLowerCase(),
	);

	if (skills.length === 1) {
		const skill = skills[0];
		return t("report.pointsInfo.infoText.singleSkill", {
			itemCount: numActivities,
			itemLabel: smartPluralize("item", numActivities),
			skill_1: t(`dashboard.skills.${skill.skill}`).toLowerCase(),
			skill_1_score: skill.numPointsEarned,
			skill_1_total: skill.numTotalPoints,
			proficiency: t(`session.masteryLevels.${compositeLevel}`).toLowerCase(),
			compositeFeedback,
		});
	}

	const skillsText = `${skillNames.slice(0, -1).join(", ")} ${t("common.and")} ${skillNames[skillNames.length - 1]}`;

	const skillDetails = skills
		.map((skill) =>
			t("report.pointsInfo.skillDetails", {
				skill: t(`dashboard.skills.${skill.skill}`).toLowerCase(),
				score: skill.numPointsEarned,
				total: skill.numTotalPoints,
				proficiency: t(`session.masteryLevels.${skill.level}`).toLowerCase(),
			}),
		)
		.join(" ");

	return t("report.pointsInfo.infoText.multiSkill", {
		itemCount: numActivities,
		itemLabel: smartPluralize("item", numActivities),
		skills_text: skillsText,
		skill_details: skillDetails,
		overall_score: numPointsEarned,
		overall_total: numTotalPoints,
		overall_proficiency: t(
			`session.masteryLevels.${compositeLevel}`,
		).toLowerCase(),
		compositeFeedback,
	});
};

const getSummaryCardData = (response: ReportDetails, t: TFunction) => {
	const getTitle = () => {
		switch (response.reportSessionType) {
			case "DIAGNOSTIC":
				return t("report.diagnosticSessionCompleted");
			case "SUMMATIVE":
				return t("report.diagnosticSessionCompleted");
			case "PRACTICETEST":
				return t("report.practiceTestCompleted");
			case "STUDYPACK":
				return t("report.studySessionCompleted");
			default:
				return t("report.studySessionCompleted");
		}
	};

	const title = getTitle();

	const summaryCardData = {
		title,
		score: Math.round(
			(response.numPointsEarned / response.numTotalPoints) * 30,
		),
		numPointsEarned: response.numPointsEarned,
		numTotalPoints: response.numTotalPoints,
		numItems: response.numQuestions,
		stats: [
			{
				overlineText: t("report.answered"),
				icon: "check-all",
				value: `${response.answered}`,
				tooltip: t("report.answeredTooltip"),
			},
			{
				overlineText: t("report.timeElapsed"),
				icon: "timer-sand",
				value: t("report.timeElapsedValue", {
					time: response.timeElapsed.toFixed(0),
					sec: smartPluralize("sec", response.timeElapsed),
				}),
				tooltip: t("report.timeElapsedTooltip"),
			},
			// {
			// 	overlineText: t("report.accuracy"),
			// 	icon: "radar",
			// 	value: `${Math.round(response.accuracy)}%`,
			// 	tooltip: t("report.accuracyTooltip"),
			// },
			// {
			// 	overlineText: t("report.duration"),
			// 	icon: "timer-outline",
			// 	value: formatSecondsToUTCtime(response.timeElapsed),
			// 	tooltip: t("report.durationTooltip"),
			// },
			{
				overlineText: t("report.avgSpeed"),
				icon: "speedometer",
				value: t("report.avgSpeedValue", {
					speed: response.avgSpeed.toFixed(0),
					sec: smartPluralize("sec", response.avgSpeed),
				}),
				tooltip: t("report.avgSpeedTooltip"),
			},
			...(response.numHints != null
				? [
						{
							overlineText: t("report.hintsUsed", {
								hint: smartPluralize("hint", response.numHints),
							}),
							icon: "lightbulb-on-outline",
							value: `${response.numHints}`,
							tooltip: t("report.hintsUsedTooltip"),
						},
					]
				: []),
		],
	};
	return summaryCardData;
};
const getPerformanceCardData = (response: ReportDetails, t: TFunction) => {
	const performanceCardData = {
		title: t("report.performance.title"),
		subtitle: t("report.performance.subtitle", {
			count: response.numQuestions,
		}),
		sections: [
			{
				sectionTitle: "",
				items: [
					{
						title: t("common.correct"),
						rightText: t("report.performance.correctItems", {
							count: response.numCorrect,
						}),
						icon: "thumb-up-outline" as IconType,
						color: theme().colors.success.main,
					},
					{
						title: t("common.incorrect"),
						rightText: t("report.performance.incorrectItems", {
							count: response.numIncorrect,
						}),
						icon: "thumb-down-outline" as IconType,
						color: theme().colors.error,
					},
				],
			},
		],
	};
	return performanceCardData;
};

const getMasteryCardData = ({
	distribution,
	numMarked,
	numSkipped,
	isSummativeTest,
	numAnswered,
	t,
	theme,
}: {
	distribution: ReportDetails["distribution"];
	numMarked?: number;
	numSkipped?: number;
	isSummativeTest?: boolean;
	numAnswered?: number;
	t: TFunction;
	theme: AppTheme;
}) => {
	if (!distribution) {
		return null;
	}

	const title = isSummativeTest
		? t("report.mastery.performanceTitle")
		: t("report.mastery.sessionMasteryTitle");

	const practiceTestMasteryCardData = {
		title: t("report.mastery.performanceTitle"),
		subtitle: t("report.mastery.subtitle", { count: numAnswered }),
		sections: [
			{
				items: [
					{
						title: t("common.correct"),
						count: 0,
						icon: "thumb-up-outline" as IconType,
						type: "correct" as const,
						color: theme.colors.progress.high,
					},
					{
						title: t("common.incorrect"),
						type: "incorrect" as const,
						count: 0,
						icon: "thumb-down-outline" as IconType,
						color: theme.colors.progress.low,
					},
					...(numMarked != null
						? [
								{
									icon: "flag-outline" as IconType,
									title: t("common.marked"),
									count: numMarked,
									type: "marked" as const,
									color: theme.colors.secondary,
								},
							]
						: []),
					...(numSkipped != null
						? [
								{
									title: t("common.skipped"),
									count: numSkipped,
									type: "skipped" as const,
									icon: "minus-circle-outline" as IconType,
									color: theme.colors.progress.noneBackground,
								},
							]
						: []),
				],
			},
		],
	};

	const masteryCardData = {
		title,
		subtitle: t("report.mastery.subtitle", {
			count: numAnswered,
			item: smartPluralize("item", numAnswered || 0),
		}),
		sections: [
			{
				items: sortedMasteryLevels.map((item) => ({
					title: t(item.alias.title),
					count: distribution[item.id],
					icon: item.icon as IconType,
					color: theme.dark ? item.darkColor : item.lightColor,
				})),
			},
		],
	};

	return isSummativeTest ? practiceTestMasteryCardData : masteryCardData;
};
const getReviewListItemData = ({
	distribution,
	isSummativeTest,
	numSkipped,
	numMarked,
	numAnswered,
	t,
	theme,
}: {
	distribution: ReportDetails["distribution"];
	numAnswered?: number;
	numSkipped?: number;
	numMarked?: number;
	isSummativeTest?: boolean;
	t: TFunction;
	theme: AppTheme;
}) => {
	const reviewListItemsDataForQuestion = getMasteryCardData({
		distribution,
		isSummativeTest,
		numMarked,
		numSkipped,
		t,
		numAnswered,
		theme,
	});

	return {
		QUESTION: {
			items: reviewListItemsDataForQuestion,
			numIncorrect: distribution.low,
		},
	};
};

const getReportData = (
	response: ReportDetails,
	t: TFunction,
	theme: AppTheme,
	isSummativeTest?: boolean,
) => {
	const {
		markedCount: numMarked,
		numSkipped,
		numIncorrect,
		numCorrect,
		examName,
	} = response;

	const summaryCardData =
		isScreenshotMode && !isMockedData
			? mockedSummaryCardData
			: getSummaryCardData(response, t);

	const performanceCardData = getPerformanceCardData(response, t);
	const masteryCardData =
		isScreenshotMode && !isMockedData
			? mockedMasteryCardData
			: getMasteryCardData({
					distribution: response.distribution,
					numMarked,
					numSkipped,
					isSummativeTest,
					t,
					numAnswered: response.answered,
					theme,
				});
	const reviewListItemsData = response.distribution
		? getReviewListItemData({
				distribution: response.distribution,
				isSummativeTest,
				numSkipped,
				numMarked,
				t,
				numAnswered: response.answered,
				theme,
			})
		: null;
	const masteryTagProgressData =
		isScreenshotMode && !isMockedData
			? mockedTagProgressData
			: getMasteryTagsProgressData({
					tags: response.tagDetails,
					currentView: "Progress",
					numItems: response.answered,
					t,
					theme,
				});
	const itemTypeProgressData = getItemTypeProgressData(response.tagDetails, t);

	const skills = response.tagDetails.map((tag) => ({
		skill: tag.label as Skill,
		numPointsEarned: tag.numPointsEarned || 0,
		numTotalPoints: tag.numTotalPoints || 0,
	}));

	const compositeScoreAndLevel = getCompositeScoreAndLevel(skills, t, theme);
	const sessionReportInfoText = isMockedData
		? mockedReport.sessionSummaryAlertBody(
				response.tagDetails.map((tag) => tag.label!),
			)
		: getSessionReportInfoText({
				numActivities: response.numQuestions,
				numPointsEarned: response.numPointsEarned,
				numTotalPoints: response.numTotalPoints,
				skills: compositeScoreAndLevel.skillDetails,
				compositeLevel: compositeScoreAndLevel.compositeLevel,
				compositeFeedback: compositeScoreAndLevel.compositeFeedback,
				t,
			});

	return {
		summaryCardData: {
			...summaryCardData,
			compositeColor: compositeScoreAndLevel.compositeColor,
			compositeScore: compositeScoreAndLevel.compositeScaledScore,
			sessionReportInfoText,
		},
		performanceCardData,
		masteryTagProgressData,
		masteryCardData,
		reviewListItemsData,
		numCorrect,
		numIncorrect,
		sessionId: response.id,
		sessionTitle: examName,
		itemTypeProgressData,
	};
};

export { getReportData };
